import React, { useEffect, useReducer } from 'react';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from 'react-router-dom';
import { BreadCrumb, FooterSection, FaqSection } from '../components';
import QAPage from '../components/QAPage';
import {
  MoreAboutRoute,
  StationTrainsSection,
  TrainBetweenStationsSection,
  ReturnTrainSection,
  TrainScheduleTable,
  ClassFareTable
} from './components';
import { TrainClasses } from './TrainClasses';
import capitalise from '../../utils/capitalise';
import { formatDuration, formatDuration2 } from '../../utils/dateTimeFormatUtils';
import DataLoaderError from '../../errors/DataLoaderError';
import { RouteInfoData, WeeklyTrainInfo, EnvironmentVariables, RailwayStation } from '../types';
import { getAppEnv } from '../../../config/env';
import RouteInfoContext from './RouteInfoContext';
import { RECOMMEND_PATH } from '../components/RecommendTripPathList/utils/recommendTripPathListUtils';
import RecommendTripPathWrapper from '../components/RecommendTripPathList/RecommendTripPathList';
import { searchWidgetInitialState, searchWidgetReducer } from '../../reducers/searchWidgetReducer';
import SearchWidget from '../components/SearchWidget/SearchWidget';
import SEARCH_WIDGET_UTILS from '../components/SearchWidget/utils/utils';
import HtmlHeader from '../components/HtmlHeader';
import AppDownloadBanner from '../components/AppDownloadBanner';
import { SEO_ROUTE_PAGE } from '../../utils/RailsSEOConstants';
import RouteSeoHead from './components/RouteSeoHead';
import RouteSeoContent from './components/RouteSeoContent';
import { getOtherDestFromCityInterlinkingBlocks, getOtherSrcToCityInterlinkingBlocks } from './RouteInfoUtils';
import { logTbcPageLoadEvent } from '../../analytics/events/logTbcPageLoadEvent';
const { Provider: RouteInfoProvider } = RouteInfoContext;
const SearchWidgetContext = React.createContext<any>(searchWidgetInitialState);

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;

const BANGALORE_CHENNAI_FAQS = [
  {
    question: 'Q. Can I change my boarding station after booking?',
    answer:
      "A. Yes. Log in to MakeMyTrip, go to 'My Trips' section, select your train ticket, and choose \"Change Boarding Station.\" You can pick a new station only if it's a scheduled halt of your train, and the request must be submitted at least 24 hours before the train's original departure time. The fare stays the same, and this change can typically be made only once per ticket. Once updated, you can no longer board from your original station."
  },
  {
    question: 'Q. How can I check coach position before arrival?',
    answer:
      "A. You can check coach position on the MakeMyTrip app by entering your train number and journey date. This shows the order in which coaches will arrive on the platform, helping you stand at the right spot before the train pulls in. Coach positions can occasionally change due to rake reshuffling, so it's best to confirm again at the station via the platform display board."
  },
  {
    question: 'Q. What happens if my train ticket remains waitlisted?',
    answer:
      "A. If your ticket remains fully waitlisted after chart preparation (typically 4 hours before departure), it is automatically cancelled and the fare is refunded to your original payment method without needing to file a TDR (Ticket Deposit Receipt). You will not be permitted to board the train with a fully waitlisted ticket. If you booked at the counter, you'll need to file a TDR within the prescribed window to claim a refund."
  },
  {
    question: 'Q. Can I select my seat/berth preference?',
    answer:
      "A. You can indicate a berth preference (lower, middle, upper, or side) at the time of booking, but this is treated as a request, not a guarantee — final allocation depends on availability and the train's auto-upgradation/quota rules. Senior citizens, women travelling alone or with children, and passengers with disabilities are given priority for lower berths under applicable quotas."
  },
  {
    question: 'Q. What happens if my train is cancelled?',
    answer:
      'A. If a train is fully cancelled, ticket holders receive an automatic full refund credited to their original payment method, with no need to file a TDR. If you booked at the counter, you must file a TDR within the prescribed time limit to claim your refund.'
  },
  {
    question: 'Q. How do I check my PNR status?',
    answer:
      'A. You can check your PNR status directly on MakeMyTrip by entering your 10-digit PNR number. Your PNR status shows your current booking status (confirmed, RAC, or waitlisted) and updates as the chart is prepared closer to departure.'
  }
];

function decorateListingData(listingData, templateId, routeInfo) {
  const {
    alternateRoutePageInterlinks,
    source,
    destination,
    topFlightRoutesFromCity,
    topFlightRoutesToCity,
    cabsRoutesFromCity,
    cabsRoutesToCity,
    busRoutesFromCity,
    busRoutesToCity,
    weeklyTrainList,
    weeklyFestiveTrainList,
    trainsStartingFromSource,
    trainsPassingThroughSource,
    otherDestinationsFromSource,
    otherSourcesToDestination,
    otherDestinationsFromDestination,
    otherSourcesToSource,
    topTrainRoutes,
    nearByCities,
    hotelsInSourceCity,
    hotelsInDestinationCity,
    template,
    alternatePageUrlInfo,
    faq,
    geoFaqList,
    pageUrl,
    returnRoutePageUrl,
    moreInfoHeading,
    tokens,
    miscellaneous,
    returnRouteTitle,
    appDownloadTopStrip,
    isReturnRouteAvailable,
    ltsFromSource,
    ltsToDestination,
    stationsNearbySource,
    stationsNearbyDestination,
    busSeoRoutes,
    partialRouteImplementationConstants,
    popularTrainsFromSrcToDest,
    sourceWhere2GoInfo,
    destinationWhere2GoInfo,
    dailyTrainListSeo,
    weeklyTrainListSeo,
    classAndFare
  } = listingData;

  const fromCity = tokens.source;
  const fromCityCode = routeInfo.fromCity.code;
  const toCity = tokens.destination;
  const toCityCode = routeInfo.toCity.code;

  const stationMap: Record<string, RailwayStation> = {};
  const trainsMap: Record<string, WeeklyTrainInfo> = {};
  const trainsBetweenStations: string[] = [];

  source.obj.railwayStations.forEach((station: RailwayStation) => {
    station.name = station.name.replace('Junction', 'Jn');
    station.name = station.name.replace('Railway Station', '');
    stationMap[station.code] = station;
  });

  destination.obj.railwayStations.forEach((station: RailwayStation) => {
    station.name = station.name.replace('Junction', '');
    stationMap[station.code] = station;
  });

  weeklyTrainList.list.forEach((train: WeeklyTrainInfo) => {
    train.durationFormatted = formatDuration(train.duration);
    train.durationParts = formatDuration2(train.duration);
    trainsMap[train.trainNumber] = train;
    trainsBetweenStations.push(train.trainNumber);
  });

  weeklyFestiveTrainList.list.forEach((train: WeeklyTrainInfo) => {
    // if the current format is in format say 5hr, make it 5hr 0mins
    if (String(train.duration).endsWith('hr')) {
      train.duration = train.duration.concat(' 0mins');
    }
    train.durationFormatted = formatDuration(train.duration);
    train.durationParts = formatDuration2(train.duration.replace('hr ', ':').replace('mins', ''));
    return train;
  });
  const otherSrcToDest = getOtherSrcToCityInterlinkingBlocks(otherSourcesToDestination, toCity);
  const otherDestFromSrc = getOtherDestFromCityInterlinkingBlocks(otherDestinationsFromSource, fromCity);
  const otherDestFromDest = getOtherDestFromCityInterlinkingBlocks(otherDestinationsFromDestination, fromCity);
  const otherSrcToSrc = getOtherSrcToCityInterlinkingBlocks(otherSourcesToSource, toCity);

  return {
    alternateRoutePageInterlinks,
    source,
    destination,
    faq,
    geoFaqList,
    template,
    fromCity,
    toCity,
    fromCityCode,
    toCityCode,
    stationMap,
    trainsBetweenStations,
    trainsMap,
    cabsRoutesFromCity,
    cabsRoutesToCity,
    busRoutesFromCity,
    busRoutesToCity,
    trainsFromSource: trainsStartingFromSource,
    trainsThroughSource: trainsPassingThroughSource,
    otherSrcToDest,
    otherDestFromSrc,
    otherDestFromDest,
    otherSrcToSrc,
    topTrainRoutes,
    nearByCities,
    hotelsInSourceCity,
    hotelsInDestinationCity,
    routeInfo,
    pageUrl,
    returnRoutePageUrl,
    topFlightRoutesFromCity,
    topFlightRoutesToCity,
    weeklyFestiveTrainList,
    weeklyTrainList,
    moreInfoHeading,
    tokens,
    miscellaneous,
    returnRouteTitle,
    templateId,
    alternatePageUrlInfo,
    appDownloadTopStrip,
    isReturnRouteAvailable,
    ltsFromSource,
    ltsToDestination,
    stationsNearbySource,
    stationsNearbyDestination,
    busSeoRoutes,
    partialRouteImplementationConstants,
    popularTrainsFromSrcToDest,
    sourceWhere2GoInfo,
    destinationWhere2GoInfo,
    dailyTrainListSeo,
    weeklyTrainListSeo,
    classAndFare
  };
}

type PathParamsType = {
  fromCity: string;
  toCity: string;
};

type RouteInfoProps = RouteComponentProps<PathParamsType> & {
  data: RouteInfoData;
};

const RouteInfo = ({ data }: RouteInfoProps) => {
  const [searchWidgetState, searchWidgetDispatch] = useReducer(searchWidgetReducer, searchWidgetInitialState);
  const { onTrainClicked, onTrainExtraSectionClicked, updateCityNameWithFilter, onMountInitSearchWidgetWrapper } =
    SEARCH_WIDGET_UTILS.ROUTE_INFO_UTILS(searchWidgetDispatch);
  const { fromFilter, toFilter } = searchWidgetState;
  const { source: vernacularSourceCityName, destination: vernacularDestinationCityName } = data.tokens;
  const isBangaloreToChennai = data?.fromCityCode === 'SBC' && data?.toCityCode === 'MAS';

  const mergedGeoFaqList = isBangaloreToChennai
    ? [...(data.geoFaqList?.list || []), ...BANGALORE_CHENNAI_FAQS]
    : data.geoFaqList?.list ?? [];

  // ── Dynamic LD+JSON for the 5 older API-driven sections ──────────────────
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataAny = data as any;
  const originatingRaw = dataAny.trainsFromSource;
  const passingRaw = dataAny.trainsThroughSource;

  const originatingTrains: any[] = Array.isArray(originatingRaw)
    ? [].concat(...originatingRaw.map((g: any) => g?.list || []))
    : originatingRaw?.list || [];

  const passingTrains: any[] = Array.isArray(passingRaw)
    ? [].concat(...passingRaw.map((g: any) => g?.list || []))
    : passingRaw?.list || [];

  const festiveSectionSchema =
    isBangaloreToChennai && data.weeklyFestiveTrainList?.list?.length > 0
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: data.weeklyFestiveTrainList.title || 'Festive Special Trains from Bangalore to Chennai',
          numberOfItems: data.weeklyFestiveTrainList.list.length,
          itemListElement: data.weeklyFestiveTrainList.list.map((train, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'TrainTrip',
              name: train.trainName,
              trainNumber: train.trainNumber,
              departureStation: { '@type': 'TrainStation', name: 'KSR Bengaluru City Junction', identifier: 'SBC' },
              arrivalStation: { '@type': 'TrainStation', name: 'MGR Chennai Central', identifier: 'MAS' },
              departureTime: train.departureTime,
              arrivalTime: train.arrivalTime,
              provider: { '@type': 'Organization', name: 'Indian Railways' }
            }
          }))
        })
      : null;

  const originatingSchema =
    isBangaloreToChennai && originatingTrains.length > 0
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: `Trains Originating from ${data.fromCity}`,
          numberOfItems: originatingTrains.length,
          itemListElement: originatingTrains.map((train: any, i: number) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'TrainTrip',
              trainNumber: train.trainNumber,
              departureStation: { '@type': 'TrainStation', identifier: train.sourceStn },
              arrivalStation: { '@type': 'TrainStation', identifier: train.destinationStn },
              departureTime: train.departureTime,
              arrivalTime: train.arrivalTime,
              provider: { '@type': 'Organization', name: 'Indian Railways' }
            }
          }))
        })
      : null;

  const passingSchema =
    isBangaloreToChennai && passingTrains.length > 0
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: `Trains Passing Through ${data.fromCity}`,
          numberOfItems: passingTrains.length,
          itemListElement: passingTrains.map((train: any, i: number) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'TrainTrip',
              trainNumber: train.trainNumber,
              departureStation: { '@type': 'TrainStation', identifier: train.sourceStn },
              arrivalStation: { '@type': 'TrainStation', identifier: train.destinationStn },
              departureTime: train.departureTime,
              arrivalTime: train.arrivalTime,
              provider: { '@type': 'Organization', name: 'Indian Railways' }
            }
          }))
        })
      : null;

  const weeklyTimetableSchema =
    isBangaloreToChennai && (data.weeklyTrainListSeo?.list?.length ?? 0) > 0
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: data.weeklyTrainListSeo?.title || 'Weekly Train Timetable from Bangalore to Chennai',
          numberOfItems: data.weeklyTrainListSeo?.list?.length,
          itemListElement: data.weeklyTrainListSeo?.list?.map((train, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'TrainTrip',
              name: train.trainName,
              trainNumber: train.trainNumber,
              departureStation: {
                '@type': 'TrainStation',
                name: train.fromCityName || 'KSR Bengaluru City Junction',
                identifier: train.fromStnCode || 'SBC'
              },
              arrivalStation: {
                '@type': 'TrainStation',
                name: train.toCityName || 'MGR Chennai Central',
                identifier: train.toStnCode || 'MAS'
              },
              departureTime: train.departureTime,
              arrivalTime: train.arrivalTime,
              provider: { '@type': 'Organization', name: 'Indian Railways' }
            }
          }))
        })
      : null;

  const dailyTimetableSchema =
    isBangaloreToChennai && (data.dailyTrainListSeo?.list?.length ?? 0) > 0
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: data.dailyTrainListSeo?.title || 'Daily Train Timings from Bangalore to Chennai',
          numberOfItems: data.dailyTrainListSeo?.list?.length,
          itemListElement: data.dailyTrainListSeo?.list?.map((train, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'TrainTrip',
              name: train.trainName,
              trainNumber: train.trainNumber,
              departureStation: {
                '@type': 'TrainStation',
                name: train.fromCityName || 'KSR Bengaluru City Junction',
                identifier: train.fromStnCode || 'SBC'
              },
              arrivalStation: {
                '@type': 'TrainStation',
                name: train.toCityName || 'MGR Chennai Central',
                identifier: train.toStnCode || 'MAS'
              },
              departureTime: train.departureTime,
              arrivalTime: train.arrivalTime,
              provider: { '@type': 'Organization', name: 'Indian Railways' }
            }
          }))
        })
      : null;

  useEffect(() => {
    updateCityNameWithFilter({ fromFilter, toFilter, data });
  }, [fromFilter, toFilter]);

  useEffect(() => {
    if (typeof window !== 'undefined' && data) {
      logTbcPageLoadEvent({
        fromCity: data?.fromCity,
        fromCityCode: data?.fromCityCode,
        toCity: data?.toCity,
        toCityCode: data?.toCityCode,
        trainsMap: data?.trainsMap,
        weeklyFestiveTrainList: data?.weeklyFestiveTrainList,
        weeklyCovidTrainList: data?.weeklyCovidTrainList
      });
    }
  }, []);

  return (
    <RouteInfoProvider
      value={{
        ...data,
        onTrainClicked: onTrainClicked,
        onTrainExtraSectionClicked: onTrainExtraSectionClicked
      }}
    >
      <div>
        <AppDownloadBanner {...data.template} />
        <SearchWidgetContext.Provider value={{ searchWidgetState, searchWidgetDispatch }}>
          <SearchWidget
            openDatePickerOnMount={true}
            topFix={true}
            classNode={TrainClasses.FC}
            templateId={data.templateId}
            searchButtonTitle={data.miscellaneous['SELECT DATE AND CHECK AVAILABILITY']}
            onMountInitSearchWidget={onMountInitSearchWidgetWrapper(data)}
            pageWiseContext={SearchWidgetContext}
            pageName={SEO_ROUTE_PAGE}
          />
        </SearchWidgetContext.Provider>
        <div className="container">
          <HtmlHeader data={data} containsAlternateHref={true} />
          {isBangaloreToChennai && <RouteSeoHead />}
          {(festiveSectionSchema ||
            originatingSchema ||
            passingSchema ||
            weeklyTimetableSchema ||
            dailyTimetableSchema) && (
            <Helmet>
              {festiveSectionSchema && <script type="application/ld+json">{festiveSectionSchema}</script>}
              {originatingSchema && <script type="application/ld+json">{originatingSchema}</script>}
              {passingSchema && <script type="application/ld+json">{passingSchema}</script>}
              {weeklyTimetableSchema && <script type="application/ld+json">{weeklyTimetableSchema}</script>}
              {dailyTimetableSchema && <script type="application/ld+json">{dailyTimetableSchema}</script>}
            </Helmet>
          )}
          <BreadCrumb breadcrumb={data.template.breadcrumb} />
          <TrainBetweenStationsSection />
          {data.isReturnRouteAvailable && (
            <ReturnTrainSection
              fromCity={data.fromCity}
              toCity={data.toCity}
              title={data.returnRouteTitle
                .replace('${source}', vernacularSourceCityName)
                .replace('${destination}', vernacularDestinationCityName)}
              url={data.returnRoutePageUrl}
            />
          )}
          <StationTrainsSection trainListType={'originating'} />
          <StationTrainsSection trainListType={'passing'} />
          {!isBangaloreToChennai &&
            data.classAndFare &&
            data.classAndFare.list &&
            data.classAndFare.list.length > 0 && (
              <ClassFareTable
                title={data.classAndFare.title}
                seoTitle={data.classAndFare.seoTitle}
                headers={data.classAndFare.headers}
                list={data.classAndFare.list}
              />
            )}
          {data.weeklyTrainListSeo && data.weeklyTrainListSeo.list && data.weeklyTrainListSeo.list.length > 0 && (
            <TrainScheduleTable
              title={data.weeklyTrainListSeo.title}
              seoTitle={data.weeklyTrainListSeo.seoTitle}
              headers={data.weeklyTrainListSeo.headers}
              list={data.weeklyTrainListSeo.list}
              type="weekly"
            />
          )}
          {data.dailyTrainListSeo && data.dailyTrainListSeo.list && data.dailyTrainListSeo.list.length > 0 && (
            <TrainScheduleTable
              title={data.dailyTrainListSeo.title}
              seoTitle={data.dailyTrainListSeo.seoTitle}
              headers={data.dailyTrainListSeo.headers}
              list={data.dailyTrainListSeo.list}
              type="daily"
            />
          )}
          {!isBangaloreToChennai && (
            <FaqSection
              faq={data.faq.list}
              pageUrl={data.pageUrl}
              title={data.faq.title}
              bookNowString={data.miscellaneous['BOOK NOW']}
              pageName={SEO_ROUTE_PAGE}
            />
          )}
          {isBangaloreToChennai && <RouteSeoContent />}
          {data.geoFaqList?.list && data.geoFaqList.list.length > 0 && (
            <>
              {isBangaloreToChennai && (
                <Helmet>
                  <script type="application/ld+json">
                    {JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'FAQPage',
                      name: data.geoFaqList?.title,
                      mainEntity: mergedGeoFaqList.map((item) => ({
                        '@type': 'Question',
                        name: item.question.replace(/<[^>]*>/g, '').replace(/^Q\.\s*/i, ''),
                        acceptedAnswer: {
                          '@type': 'Answer',
                          text: item.answer.replace(/<[^>]*>/g, '').replace(/^A\.\s*/i, '')
                        }
                      }))
                    })}
                  </script>
                </Helmet>
              )}
              <QAPage
                faq={mergedGeoFaqList}
                pageUrl={data.pageUrl}
                title={data.geoFaqList?.title}
                bookNowString={data.miscellaneous['BOOK NOW']}
                pageName={SEO_ROUTE_PAGE}
              />
            </>
          )}
          <section style={{ flexWrap: 'wrap' }} className="railSeoWrap__stnDetails makeFlex">
            {<RecommendTripPathWrapper recommendPaths={RECOMMEND_PATH.ROUTE_INFO_PAGE(data)} />}
          </section>

          <section className="bottomItems">
            {data.template.extra_info && (
              <MoreAboutRoute
                fromCity={data.fromCity}
                toCity={data.toCity}
                extraInfo={data.template.extra_info}
                title={data.moreInfoHeading
                  .replace('${source}', vernacularSourceCityName)
                  .replace('${destination}', vernacularDestinationCityName)}
              />
            )}
          </section>
        </div>
        <FooterSection template={data.template} />
      </div>
    </RouteInfoProvider>
  );
};

RouteInfo.fetchData = async (_, match, urlObj) => {
  const {
    params: { fromCity, toCity, templateId }
  } = match;
  const [fromCityName, fromCityCode] = fromCity.split('-');
  const [toCityName, toCityCode] = toCity.split('-');
  const url = `${DATA_LOADER_URL}/api/routeTemplateData/${templateId}/${fromCityName}/${toCityName}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    throw new Error('JSON parse exception: ' + textResponse);
  }
  // eslint-disable-next-line no-console
  console.log('responseData.status', responseData.status);
  if (responseData.status !== 'SUCCESS') {
    throw new DataLoaderError(`Unable to fetch for ${fromCityName} ${toCityName}`, responseData.error);
  }
  try {
    return decorateListingData(responseData, templateId, {
      fromCity: {
        name: capitalise(fromCityName),
        code: fromCityCode.toUpperCase()
      },
      toCity: { name: capitalise(toCityName), code: toCityCode.toUpperCase() }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('err', e);
  }
};

export default RouteInfo;
